// Which storage containers belong to which customer.
//
// A microgrid is one storage container named `<prefix>-<name>`, where the
// prefix is the customer's email lowercased with every "@" and "." turned
// into "-" (useAddMicrogrid builds it that way). One storage account holds
// every customer of a branch, and the sim checks only that a token may use the
// storage account, not the container, so these rules are what keeps one
// customer's Microgrids list, and its Delete, off another customer's data.
//
// The mapping is lossy, so a plain startsWith is not enough:
// - bob@x.co gives "bob-x-co" and bob@x.com gives "bob-x-com": without the
//   "-" boundary, "bob-x-com-site" would count as bob@x.co's.
// - bob@x.co.uk gives "bob-x-co-uk", and its "bob-x-co-uk-site" also starts
//   with "bob-x-co-". Only the branch's full customer list tells the two apart:
//   a container belongs to the longest prefix it starts with.
// - Two emails can even give the same prefix (bob.x@co.com and bob@x.co.com).
//   Their containers can't be told apart at all.

export function customerPrefixFor(email) {
  return email ? email.toLowerCase().replace(/[@.]/g, "-") : "";
}

const startsWithPrefix = (container, prefix) =>
  !!prefix && container.startsWith(`${prefix}-`);

// Whether the Microgrids list for `customerEmail` shows `container`.
//
// `branchEmails` is the emails of the branch's customers, or null while that
// list is loading or if it failed to load. Without it only the "-" boundary is
// checked, which can't tell bob@x.co's containers from bob@x.co.uk's. With it,
// the container is listed only under the longest prefix it starts with.
// An empty email lists nothing.
export function isListedFor(container, customerEmail, branchEmails) {
  const prefix = customerPrefixFor(customerEmail);
  if (!startsWithPrefix(container, prefix)) return false;
  if (!branchEmails) return true;
  return !branchEmails.some((email) => {
    const other = customerPrefixFor(email);
    return other.length > prefix.length && startsWithPrefix(container, other);
  });
}

// Whether `container` may be offered for deletion while viewing
// `customerEmail`. Stricter than listing: the branch's customer list must have
// loaded and include the viewed customer, and the viewed customer must be the
// only customer in it whose prefix the container starts with. So a container
// whose name fits two customers (bob@x.co's "uk-site" and bob@x.co.uk's "site"
// are both "bob-x-co-uk-site") is never offered for deletion from either list.
export function isDeletableFor(container, customerEmail, branchEmails) {
  if (!branchEmails || !customerEmail) return false;
  if (!isListedFor(container, customerEmail, branchEmails)) return false;
  const viewed = customerEmail.toLowerCase();
  const owners = new Set(
    branchEmails
      .filter((email) => startsWithPrefix(container, customerPrefixFor(email)))
      .map((email) => email.toLowerCase()),
  );
  return owners.size === 1 && owners.has(viewed);
}
